import { Fragment } from 'react';

import cash from '~/assets/icons/cash.svg';
import info from '~/assets/icons/info_2.svg';
import { CurrencyIcon } from '~/components/collections';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { Currency, formatTime } from '~/shared';
import { maskCardNumber } from '~/utils/cardNumber';
import arrow from '../../assets/images/arrow.svg';
import expensesBg from '../../assets/images/expenses_bg.svg';
import incomeBg from '../../assets/images/income_bg.svg';
import { SectionHeading } from '../SectionHeading';
import {
	StyledContainer,
	StyledExpensesBottomContainer,
	StyledExpensesContainer,
	StyledInfoContainer,
	StyledInfoRow,
	StyledPenaltiesBottomContainer,
	StyledPenaltiesContainer,
	StyledPenaltiesHeadingContainer,
	StyledSectionContainer,
	StyledStatsAmountContainer,
	StyledStatsContainer,
	StyledWarningContainer,
	StyledWarningDots,
} from './PlayerProfile.styled';

const formatTimestamp = (timestamp: number): JSX.Element => {
	const formatted = formatTime(timestamp, '{dd}/{MM}/{yy}', {
		useMoscowTimezone: true,
	});
	const parts = formatted.split('/');

	return (
		<Fragment>
			{parts[0]} <span style={{ opacity: 0.5 }}>/</span> {parts[1]}{' '}
			<span style={{ opacity: 0.5 }}>/</span> {parts[2]}
		</Fragment>
	);
};

const PlayerProfile: React.FC = () => {
	const nickname = useObservable(rage.player.getNickname());
	const realTime = useObservable(rage.game.getRealTime());
	const bankStats = useObservable(rage.bank.getBankStats());
	const cardNumber = useObservable(rage.bank.getCardNumber());
	const penalties = 0; // TODO: Удалить штрафы
	const rubleIcon = useWalletIcon(Currency.Ruble, true);

	return (
		<StyledContainer>
			<StyledWarningContainer>
				<img src={info} style={{ opacity: 0.5 }} />
				<Text
					style={{
						fontSize: 16,
						fontWeight: 400,
						color: '#FFFFFF',
						fontStyle: 'italic',
						opacity: 0.5,
					}}
				>
					Остерегейтесь мошенников и не переводите им деньги, если вы в этом не
					уверены!
				</Text>
				<StyledWarningDots>
					<div className="dot" />
					<div className="dot" />
					<div className="dot" />
				</StyledWarningDots>
			</StyledWarningContainer>
			<StyledSectionContainer>
				<SectionHeading icon={cash} text={'Ваши траты'} />
				<StyledStatsContainer>
					<StyledExpensesContainer
						style={{ backgroundImage: `url(${expensesBg})` }}
					>
						<Text style={{ fontSize: 16, color: '#ffffff', opacity: 0.1 }}>
							{realTime?.month}
						</Text>
						<Text
							style={{
								fontSize: 16,
								color: '#FFFFFF',
								opacity: 0.25,
								marginTop: 10,
							}}
						>
							Траты за месяц
						</Text>
						<StyledExpensesBottomContainer>
							<StyledStatsAmountContainer>
								<Text
									style={{
										fontSize: 24,
										fontWeight: 500,
										color: '#FFFFFF',
									}}
								>
									{bankStats.spent.toLocaleString('ru-RU')}
								</Text>
								<CurrencyIcon icon={rubleIcon} size="lg" />
							</StyledStatsAmountContainer>
							{/* <StatsLine paymentSum={1000} purchaseSum={2000} /> */}
						</StyledExpensesBottomContainer>
					</StyledExpensesContainer>
					<StyledExpensesContainer
						style={{ backgroundImage: `url(${incomeBg})` }}
					>
						<Text style={{ fontSize: 16, color: '#ffffff', opacity: 0.1 }}>
							{realTime?.month}
						</Text>
						<Text
							style={{
								fontSize: 16,
								color: '#FFFFFF',
								opacity: 0.25,
								marginTop: 10,
							}}
						>
							Доходы за месяц
						</Text>
						<StyledExpensesBottomContainer>
							<StyledStatsAmountContainer>
								<Text
									style={{
										fontSize: 24,
										fontWeight: 500,
										color: '#FFFFFF',
									}}
								>
									{bankStats.earned.toLocaleString('ru-RU')}
								</Text>
								<CurrencyIcon icon={rubleIcon} size="lg" />
							</StyledStatsAmountContainer>
							{/* <StatsLine paymentSum={1000} purchaseSum={2000} /> */}
						</StyledExpensesBottomContainer>
					</StyledExpensesContainer>
					<StyledPenaltiesContainer>
						<StyledPenaltiesHeadingContainer>
							<Text
								style={{
									fontSize: '14px',
									fontWeight: 400,
									color: '#FFFFFF',
									opacity: 0.25,
								}}
							>
								Штрафы
							</Text>
							<img
								src={arrow}
								style={{ opacity: 0.5, pointerEvents: 'none' }}
							/>
						</StyledPenaltiesHeadingContainer>
						<StyledPenaltiesBottomContainer>
							<StyledStatsAmountContainer>
								<Text
									style={{
										fontSize: '18px',
										fontWeight: 400,
										color: '#FFFFFF',
									}}
								>
									{penalties.toLocaleString('ru-RU')}
								</Text>
								<CurrencyIcon icon={rubleIcon} size="lg" />
							</StyledStatsAmountContainer>
							<Text
								style={{
									fontSize: '12px',
									fontWeight: 400,
									color: '#FFFFFF',
									opacity: 0.5,
								}}
							>
								Нажмите чтобы оплатить
							</Text>
						</StyledPenaltiesBottomContainer>
					</StyledPenaltiesContainer>
				</StyledStatsContainer>
			</StyledSectionContainer>
			<StyledSectionContainer>
				<SectionHeading icon={info} text={'Ваша информация'} />
				<StyledInfoContainer>
					<StyledInfoRow>
						<Text>Владелец карты</Text>
						<Text>{nickname}</Text>
					</StyledInfoRow>
					<Divider $direction="H" style={{ opacity: 0.1 }} />
					<StyledInfoRow>
						<Text>Дата создания</Text>
						<Text>{formatTimestamp(bankStats.registeredAt)}</Text>
					</StyledInfoRow>
					<Divider $direction="H" style={{ opacity: 0.1 }} />
					<StyledInfoRow>
						<Text>Номер карты</Text>
						<Text>{maskCardNumber(cardNumber)}</Text>
					</StyledInfoRow>
					<Divider $direction="H" style={{ opacity: 0.1 }} />
					<StyledInfoRow>
						<Text>Срок действия</Text>
						<Text>{formatTimestamp(bankStats.cardExpiresAt)}</Text>
					</StyledInfoRow>
				</StyledInfoContainer>
			</StyledSectionContainer>
		</StyledContainer>
	);
};

export { PlayerProfile };
